import { ofType as ngrxOfType } from '@ngrx/effects';
import { Action, ActionCreator } from '@ngrx/store';
import { filter, OperatorFunction } from 'rxjs';

type FilteredAction<Creator extends ActionCreator = ActionCreator> = {
  action: Creator;
  filter(action: ReturnType<Creator> & Action): boolean;
};

type Rule = ActionCreator | FilteredAction;

type CreatorOf<RuleType> = RuleType extends ActionCreator
  ? RuleType
  : RuleType extends { action: infer Creator extends ActionCreator }
    ? Creator
    : never;

type CheckedRule<RuleType> = RuleType extends ActionCreator
  ? RuleType
  : {
      action: CreatorOf<RuleType>;
      filter: (action: ReturnType<CreatorOf<RuleType>>) => boolean;
    };

/**
 * extends the functionality of the original ngrx `ofType` by adding a new type of input:
 *
 * ```ts
 * actions$.pipe(
 *   ofTypeWithFilter(
 *     someActions.opened,
 *     { action: someActions.entitiesLoaded, filter: hasEntities }
 *   ),
 * );
 * ```
 *
 * the effect will trigger on that action only if the filter condition is respected
 */
export function ofTypeWithFilter<const Rules extends readonly Rule[]>(
  ...rules: Rules & {
    [Index in keyof Rules]: CheckedRule<Rules[Index]>;
  }
): OperatorFunction<Action, ReturnType<CreatorOf<Rules[number]>>> {
  const creators = rules.map((rule) => ('action' in rule ? rule.action : rule));

  return (source$) =>
    source$.pipe(
      ngrxOfType<ReturnType<CreatorOf<Rules[number]>> & Action>(...creators),
      filter((action) =>
        rules.some((rule) => {
          const creator = 'action' in rule ? rule.action : rule;

          return creator.type === action.type && (!('filter' in rule) || rule.filter(action));
        })
      )
    );
}
